import { io, Socket } from 'socket.io-client';
import { BACKEND_URL } from './env';
import { SOCKET_EVENTS, orderRoomName } from '../../shared/socketEvents';
import { analytics } from '../app/services/analyticsService';

let socket: Socket | null = null;
const joinedOrderRooms = new Set<string>();

type SocketRole = 'customer' | 'driver' | 'admin' | 'owner';

type JoinableOrderRef = {
  orderId: string;
  customerId?: string | null;
  driverId?: string | null;
};

interface SocketAuthState {
  accessToken: string | null;
  sessionId: string | null;
  role: SocketRole | null;
  userId: string | null;
}

const socketAuthState: SocketAuthState = {
  accessToken: null,
  sessionId: null,
  role: null,
  userId: null
};

type ListenerCleanup = () => void;

type AuthSessionRevokedPayload = {
  userId: string;
  reason: string;
  source: 'refresh_reuse' | 'user_revoke_all' | 'security_event';
  at: string;
};

type SessionRevokedHandler = (payload: AuthSessionRevokedPayload) => void;
let sessionRevokedHandler: SessionRevokedHandler | null = null;

export const registerSessionRevokedHandler = (handler: SessionRevokedHandler | null): void => {
  sessionRevokedHandler = handler;
};

const normalizeRole = (value: string | undefined): SocketRole | null => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'customer' || normalized === 'driver' || normalized === 'admin' || normalized === 'owner') {
    return normalized;
  }
  return null;
};

type DriverNotFoundPayload = { orderId: string; items: unknown[] };
type ReturnUpcsPayload = { upcs: string[]; eligibilityCache: Record<string, unknown> | null };
type ReceiptCaptureDeletedPayload = { captureId: string };

type OrderShoppingDecisionPayload = {
  orderId: string;
  sku: string;
  at: string;
  name?: string | null;
  workflowStatus?: string;
  decision?: string;
  source?: string;
};

type SocketEventPayloadMap = {
  [SOCKET_EVENTS.CART_UPDATED]: unknown;
  [SOCKET_EVENTS.ORDER_UPDATED]: unknown;
  [SOCKET_EVENTS.ORDER_CREATED]: unknown;
  [SOCKET_EVENTS.PRODUCT_UPDATED]: unknown;
  [SOCKET_EVENTS.DRIVER_NOT_FOUND_UPDATED]: DriverNotFoundPayload;
  [SOCKET_EVENTS.DRIVER_NOT_FOUND_DELETED]: { orderId: string };
  [SOCKET_EVENTS.RETURN_UPCS_UPDATED]: ReturnUpcsPayload;
  [SOCKET_EVENTS.RETURN_UPCS_DELETED]: void;
  [SOCKET_EVENTS.RECEIPT_CAPTURE_DELETED]: ReceiptCaptureDeletedPayload;
  [SOCKET_EVENTS.ORDER_MISSING_ITEM]: OrderShoppingDecisionPayload;
  [SOCKET_EVENTS.ORDER_DECISION_REQUIRED]: OrderShoppingDecisionPayload;
  [SOCKET_EVENTS.ORDER_DECISION_RESOLVED]: OrderShoppingDecisionPayload;
};

const on = <T>(event: string, callback: (data: T) => void): ListenerCleanup => {
  if (!socket) return () => {};
  socket.on(event, callback);
  return () => socket?.off(event, callback);
};

const normalizeOptionalValue = (value: string | null | undefined): string | null => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
};

const buildSocketAuthPayload = (): Record<string, string> => {
  const payload: Record<string, string> = {};
  if (socketAuthState.accessToken) payload.token = socketAuthState.accessToken;
  if (socketAuthState.sessionId) payload.sessionId = socketAuthState.sessionId;
  if (socketAuthState.role) payload.role = socketAuthState.role;
  if (socketAuthState.userId) payload.userId = socketAuthState.userId;
  return payload;
};

const hasSocketAuthState = (): boolean => Boolean(socketAuthState.userId && socketAuthState.role);

type SocketTelemetryAction =
  | 'socket_disconnected'
  | 'socket_connection_rejected'
  | 'socket_transport_connection_error'
  | 'socket_session_revoked_handler_failed'
  | 'socket_unauthorized_room_join_skipped';

const redactDiagnosticText = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim();
  if (!normalized) return undefined;
  return normalized
    .replace(/Bearer\s+[A-Za-z0-9._~+/-]+=*/gi, 'Bearer [redacted]')
    .replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, '[redacted-jwt]')
    .replace(/\b(access[_-]?token|refresh[_-]?token|session[_-]?id|token|session)\b\s*[:=]\s*[^\s,;]+/gi, '$1=[redacted]')
    .slice(0, 160);
};

const getSocketTransportName = (): string | undefined => {
  const transportName = socket?.io?.engine?.transport?.name;
  return typeof transportName === 'string' && transportName.trim() ? transportName : undefined;
};

const trackSocketDiagnostic = (action: SocketTelemetryAction, metadata: Record<string, unknown> = {}): void => {
  analytics.trackEvent({
    category: 'socket',
    action,
    metadata: {
      ...metadata,
      role: socketAuthState.role || 'unknown',
      connected: Boolean(socket?.connected),
      transport: getSocketTransportName()
    }
  });
};

const canJoinOrderRoom = (order: JoinableOrderRef): boolean => {
  const role = socketAuthState.role;
  const userId = String(socketAuthState.userId || '');
  if (!role || !userId) return false;
  if (role === 'admin' || role === 'owner') return true;
  if (role === 'customer') return String(order.customerId || '') === userId;
  if (role === 'driver') return String(order.driverId || '') === userId;
  return false;
};

export const setSocketAuth = (
  nextState: Partial<{ accessToken: string | null; sessionId: string | null; role: string | null; userId: string | null }>
): void => {
  const nextAccessToken = normalizeOptionalValue(nextState.accessToken);
  const nextSessionId = normalizeOptionalValue(nextState.sessionId);
  const nextUserId = normalizeOptionalValue(nextState.userId);
  const nextRole =
    typeof nextState.role === 'string' ? normalizeRole(nextState.role) : nextState.role === null ? null : undefined;

  if (nextAccessToken !== null || nextState.accessToken === null) {
    socketAuthState.accessToken = nextAccessToken;
  }
  if (nextSessionId !== null || nextState.sessionId === null) {
    socketAuthState.sessionId = nextSessionId;
  }
  if (nextRole !== undefined) {
    socketAuthState.role = nextRole;
  }
  if (nextUserId !== null || nextState.userId === null) {
    socketAuthState.userId = nextUserId;
  }

  if (socket?.connected) {
    socket.auth = buildSocketAuthPayload();
    if (!hasSocketAuthState()) {
      disconnectSocket();
    }
  }
};

export const clearSocketAuth = (): void => {
  socketAuthState.accessToken = null;
  socketAuthState.sessionId = null;
  socketAuthState.role = null;
  socketAuthState.userId = null;
  if (socket) {
    socket.auth = {};
  }
};

export const connectSocket = async (): Promise<Socket> => {
  if (socket?.connected) return socket;
  if (socket && !socket.connected) {
    socket.auth = buildSocketAuthPayload();
    socket.connect();
    return socket;
  }

  if (!hasSocketAuthState()) {
    throw new Error('[Socket] Auth handshake data missing; call setSocketAuth with userId and role.');
  }

  socket = io(`${BACKEND_URL}/orders`, {
    withCredentials: true,
    auth: buildSocketAuthPayload()
  });

  socket.on('disconnect', reason => {
    trackSocketDiagnostic('socket_disconnected', {
      reason: redactDiagnosticText(reason) || 'unknown'
    });
  });

  // Socket.IO clears server rooms on reconnect; re-emit joins for rooms we still track.
  socket.on('connect', () => {
    for (const room of joinedOrderRooms) {
      try {
        socket?.emit(SOCKET_EVENTS.ROOM_JOIN, room);
      } catch {
        /* best-effort */
      }
    }
  });

  socket.on(SOCKET_EVENTS.CONNECTION_ERROR, error => {
    trackSocketDiagnostic('socket_connection_rejected', {
      errorName: redactDiagnosticText(error?.name),
      errorMessage: redactDiagnosticText(error?.message) || 'unknown error'
    });
  });

  socket.on('connect_error', error => {
    trackSocketDiagnostic('socket_transport_connection_error', {
      errorName: redactDiagnosticText(error?.name),
      errorMessage: redactDiagnosticText(error?.message) || 'unknown transport error'
    });
  });

  socket.on(SOCKET_EVENTS.AUTH_SESSION_REVOKED, (payload: AuthSessionRevokedPayload) => {
    const targetUserId = String(payload?.userId || '').trim();
    if (targetUserId && socketAuthState.userId && targetUserId !== socketAuthState.userId) return;
    try {
      sessionRevokedHandler?.(payload);
    } catch (error) {
      trackSocketDiagnostic('socket_session_revoked_handler_failed', {
        errorName: error instanceof Error ? redactDiagnosticText(error.name) : undefined,
        errorMessage: error instanceof Error ? redactDiagnosticText(error.message) : 'non-error thrown'
      });
    }
    disconnectSocket();
    socketAuthState.accessToken = null;
    socketAuthState.sessionId = null;
    socketAuthState.role = null;
    socketAuthState.userId = null;
  });

  return socket;
};

export function disconnectSocket(): void {
  if (!socket) return;
  joinedOrderRooms.clear();
  socket.disconnect();
  socket = null;
}

export const getSocket = (): Socket | null => socket;

export function joinAuthorizedOrderRoom(order: JoinableOrderRef): boolean {
  const normalizedOrderId = String(order.orderId || '').trim();
  // Require a socket instance (connectSocket), but do not require connected —
  // track the room so the connect handler re-emits ROOM_JOIN after handshake.
  if (!socket || !normalizedOrderId) return false;

  if (!canJoinOrderRoom({ ...order, orderId: normalizedOrderId })) {
    trackSocketDiagnostic('socket_unauthorized_room_join_skipped', {
      hasOrderId: true,
      hasCustomerRef: Boolean(normalizeOptionalValue(order.customerId)),
      hasDriverRef: Boolean(normalizeOptionalValue(order.driverId))
    });
    return false;
  }

  const room = orderRoomName(normalizedOrderId);
  joinedOrderRooms.add(room);
  if (socket.connected) {
    socket.emit(SOCKET_EVENTS.ROOM_JOIN, room);
  }
  return true;
}

export function leaveAuthorizedOrderRoom(order: JoinableOrderRef): void {
  const normalizedOrderId = String(order.orderId || '').trim();
  if (!normalizedOrderId) return;
  const room = orderRoomName(normalizedOrderId);
  if (!joinedOrderRooms.has(room)) return;
  joinedOrderRooms.delete(room);
  if (socket?.connected) {
    socket.emit(SOCKET_EVENTS.ROOM_LEAVE, room);
  }
}

export const onCartUpdate = (callback: (data: SocketEventPayloadMap[typeof SOCKET_EVENTS.CART_UPDATED]) => void): ListenerCleanup =>
  on(SOCKET_EVENTS.CART_UPDATED, callback);
export const onOrderUpdate = (callback: (data: SocketEventPayloadMap[typeof SOCKET_EVENTS.ORDER_UPDATED]) => void): ListenerCleanup =>
  on(SOCKET_EVENTS.ORDER_UPDATED, callback);
export const onOrderCreated = (callback: (data: SocketEventPayloadMap[typeof SOCKET_EVENTS.ORDER_CREATED]) => void): ListenerCleanup =>
  on(SOCKET_EVENTS.ORDER_CREATED, callback);
export const onProductUpdate = (callback: (data: SocketEventPayloadMap[typeof SOCKET_EVENTS.PRODUCT_UPDATED]) => void): ListenerCleanup =>
  on(SOCKET_EVENTS.PRODUCT_UPDATED, callback);
export const onDriverNotFoundUpdate = (
  callback: (data: SocketEventPayloadMap[typeof SOCKET_EVENTS.DRIVER_NOT_FOUND_UPDATED]) => void
): ListenerCleanup =>
  on(SOCKET_EVENTS.DRIVER_NOT_FOUND_UPDATED, callback);
export const onDriverNotFoundDelete = (
  callback: (data: SocketEventPayloadMap[typeof SOCKET_EVENTS.DRIVER_NOT_FOUND_DELETED]) => void
): ListenerCleanup =>
  on(SOCKET_EVENTS.DRIVER_NOT_FOUND_DELETED, callback);
export const onReturnUpcsUpdate = (
  callback: (data: SocketEventPayloadMap[typeof SOCKET_EVENTS.RETURN_UPCS_UPDATED]) => void
): ListenerCleanup =>
  on(SOCKET_EVENTS.RETURN_UPCS_UPDATED, callback);
export const onReturnUpcsDelete = (callback: () => void): ListenerCleanup =>
  on(SOCKET_EVENTS.RETURN_UPCS_DELETED, callback);
export const onReceiptCaptureDeleted = (
  callback: (data: SocketEventPayloadMap[typeof SOCKET_EVENTS.RECEIPT_CAPTURE_DELETED]) => void
): ListenerCleanup =>
  on(SOCKET_EVENTS.RECEIPT_CAPTURE_DELETED, callback);
